import React, { FC, useEffect, useRef, useState } from 'react';
import { Box, Button, Text, VStack, useTheme } from '@chakra-ui/react';
import { useTranslate } from 'ts/common/hooks';
import { useBreakpoint } from 'ts/common/hooks';
import type { IBookingSessionType } from 'client_react/booking/types';
import BookingMarquee from './BookingMarquee';
import { BookingSessionTypeCover } from './BookingSessionTypeCover';

interface IBookingSessionTypeCardProps {
    bookingSessionType: IBookingSessionType;
    buttonText: string | null;
    onClick: () => void;
}

const BookingSessionTypeCard: FC<IBookingSessionTypeCardProps> = ({
    bookingSessionType,
    buttonText,
    onClick
}) => {
    const t = useTranslate();
    const theme = useTheme();
    const { name, hasPrice, description, price, images, duration, durationUnit } =
        bookingSessionType;
    const [isHovered, setIsHovered] = useState(false);
    const [showCarousel, setShowCarousel] = useState(false);
    const [isInViewport, setIsInViewport] = useState(false);
    const cardRef = useRef<HTMLDivElement>(null);
    const isLargeScreen = useBreakpoint();

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                setIsInViewport(entry.isIntersecting);
            },
            {
                root: null,
                rootMargin: '0px',
                threshold: 0.1
            }
        );

        if (cardRef.current) {
            observer.observe(cardRef.current);
        }

        return () => {
            if (cardRef.current) {
                observer.unobserve(cardRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (isLargeScreen) {
            setShowCarousel(isHovered);
        } else {
            setShowCarousel(isInViewport);
        }
    }, [isInViewport, isHovered, isLargeScreen]);

    const getDurationString = () => {
        if (!duration || !durationUnit) {
            return '';
        }

        const unit =
            durationUnit === 'minute'
                ? duration === 1
                    ? t('booking.brandHomepage.minute')
                    : t('booking.brandHomepage.minutes')
                : duration === 1
                ? t('booking.brandHomepage.hour')
                : t('booking.brandHomepage.hours');
        return `${duration} ${unit}`;
    };

    const hasMultipleImages = images?.length > 1;
    const textColor = theme.brand.colors.theme.secondary.base;
    const buttonTextColor = theme.brand.colors.theme.primary;

    return (
        <Box
            ref={cardRef}
            onClick={onClick}
            cursor="pointer"
            overflow="hidden"
            transition="transform 0.2s"
            height="100%"
            display="flex"
            flexDirection="column"
            onMouseEnter={() => isLargeScreen && setIsHovered(true)}
            onMouseLeave={() => isLargeScreen && setIsHovered(false)}
        >
            <Box
                position="relative"
                width="100%"
                paddingBottom="66.67%"
                overflow="hidden"
                backgroundColor={images?.[0]?.url ? 'transparent' : 'gray.100'}
            >
                {hasMultipleImages ? (
                    <Box
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                    >
                        <Box
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            opacity={showCarousel ? 1 : 0}
                            transition="opacity 0.3s ease-in-out"
                        >
                            <BookingMarquee images={images} />
                        </Box>
                        <Box
                            as="img"
                            src={images[0].url}
                            alt={name || ''}
                            position="absolute"
                            top="0"
                            left="0"
                            width="100%"
                            height="100%"
                            objectFit="contain"
                            opacity={showCarousel ? 0 : 1}
                            transition="opacity 0.3s ease-in-out"
                        />
                    </Box>
                ) : images?.[0]?.url ? (
                    <Box
                        as="img"
                        src={images[0].url}
                        alt={name || ''}
                        position="absolute"
                        top="0"
                        left="0"
                        width="100%"
                        height="100%"
                        objectFit="contain"
                    />
                ) : (
                    <Box position="absolute" top="0" left="0" width="100%" height="100%">
                        <BookingSessionTypeCover title={name} />
                    </Box>
                )}
            </Box>
            <VStack flex="1" alignItems="center" spacing={2} padding={6}>
                <Text
                    fontSize="18px"
                    fontWeight={500}
                    fontFamily={`${theme.brand.fonts.primary}`}
                    color={theme.brand.colors.user.primary}
                >
                    {name}
                </Text>
                <Text fontSize="16px" fontWeight={600} color={textColor} marginTop="auto">
                    {hasPrice ? `${price} /` : null}
                    {duration ? ` ${getDurationString()}` : null}
                </Text>
                <Text fontSize="md" color={textColor} flex="1">
                    {description}
                </Text>
            </VStack>
            <Button
                onClick={onClick}
                borderRadius="33px"
                color={buttonTextColor}
                backgroundColor={theme.brand.colors.user.primary}
                width="300px"
                alignSelf="center"
                _hover={{
                    backgroundColor: theme.brand.colors.user.primary,
                    opacity: 0.8,
                    transform: 'scale(1.02)'
                }}
                transition="all 0.2s"
            >
                {buttonText ? buttonText : t('booking.brandHomepage.primaryButtonText')}
            </Button>
        </Box>
    );
};

export default BookingSessionTypeCard;
